import React, { useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PropTypes from 'prop-types';

// Components
import { Mui, PerfectScrollbar } from 'components';

const useStyles = Mui.makeStyles(theme => ({
  lastBorder: { '& tr:last-child td': { borderBottom: 0 } },
  sortIcon: {
    color: theme.palette.primary.main,
    fontSize: 18,
    marginLeft: 4,
    flexShrink: 0,
  },
}));

function SortIndicator({ sortDirection }) {
  const classes = useStyles();

  if (sortDirection === 'asc') {
    return <Mui.Icon className={classes.sortIcon}>arrow_drop_up</Mui.Icon>;
  }

  if (sortDirection === 'desc') {
    return <Mui.Icon className={classes.sortIcon}>arrow_drop_down</Mui.Icon>;
  }

  return <Mui.Icon className={classes.sortIcon}>signal_cellular_alt</Mui.Icon>;
}

SortIndicator.propTypes = {
  sortDirection: PropTypes.oneOf([false, 'asc', 'desc']),
};

function Tanstack({
  columns,
  data,
  isPaginated = false,
  isSortable = false,
  defaultSortOption,
  defaultPaginationOption,
  pageSizes,
  columnsAlignment = {},
  customStyles = {},
  pagination: controlledPagination,
  onPaginationChange,
}) {
  // Hooks
  const classes = useStyles();

  // States
  const [sorting, setSorting] = useState(defaultSortOption || []);
  const [internalPagination, setInternalPagination] = useState(
    defaultPaginationOption || {
      pageIndex: 0,
      pageSize: 10,
    },
  );
  const isPaginationControlled = controlledPagination !== undefined && onPaginationChange;
  const pagination = isPaginationControlled ? controlledPagination : internalPagination;
  const setPagination = isPaginationControlled ? onPaginationChange : setInternalPagination;

  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting },
    onSortingChange: isSortable ? setSorting : null,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { pageSize, pageIndex } = isPaginated ? table.getState().pagination : {};
  const hasFooters =
    table
      .getFooterGroups()
      .map(group => group.headers.map(header => header.column.columnDef.footer))
      .flat()
      .filter(Boolean).length > 0;

  return (
    <Mui.Box sx={{ width: '100%' }}>
      <Mui.Paper variant="outlined">
        <PerfectScrollbar>
          <Mui.Table style={{ minWidth: customStyles?.minWidth || 550 }}>
            <Mui.TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <Mui.TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const canSortHeader = isSortable && header.column.getCanSort();

                    return (
                      <Mui.TableCell
                        key={header.id}
                        colSpan={header.colSpan}
                        align={columnsAlignment?.[header.id] || 'left'}
                        onClick={
                          canSortHeader ? header.column.getToggleSortingHandler() : undefined
                        }
                        style={{ cursor: canSortHeader ? 'pointer' : 'default' }}>
                        {header.isPlaceholder ? null : (
                          <Mui.Box
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: columnsAlignment?.[header.id] || '',
                              userSelect: canSortHeader ? 'none' : undefined,
                            }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSortHeader ? (
                              <SortIndicator sortDirection={header.column.getIsSorted()} />
                            ) : null}
                          </Mui.Box>
                        )}
                      </Mui.TableCell>
                    );
                  })}
                </Mui.TableRow>
              ))}
            </Mui.TableHead>
            <Mui.TableBody className={hasFooters || isPaginated ? null : classes.lastBorder}>
              {table.getRowModel().rows.map(row => (
                <Mui.TableRow key={row.id} hover>
                  {row.getVisibleCells().map(cell => (
                    <Mui.TableCell
                      key={cell.id}
                      size="small"
                      align={columnsAlignment?.[cell.column.id] || 'left'}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))}
            </Mui.TableBody>
            {hasFooters && (
              <Mui.TableFooter className={isPaginated ? null : classes.lastBorder}>
                {table.getFooterGroups().map(footerGroup => (
                  <Mui.TableRow key={footerGroup.id}>
                    {footerGroup.headers.map(header => (
                      <Mui.TableCell
                        key={header.id}
                        size="small"
                        align={columnsAlignment?.[header.id] || 'left'}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.footer, header.getContext())}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))}
              </Mui.TableFooter>
            )}
          </Mui.Table>
        </PerfectScrollbar>
        {isPaginated && (
          <Mui.TablePagination
            rowsPerPageOptions={[
              ...(pageSizes || [5, 10, 25]),
              { label: 'Todos', value: data.length },
            ]}
            component="div"
            count={table.getFilteredRowModel().rows.length}
            rowsPerPage={pageSize}
            page={pageIndex}
            SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
            onPageChange={(_, page) => table.setPageIndex(page)}
            onRowsPerPageChange={e => {
              const size = e.target.value ? Number(e.target.value) : 10;
              table.setPageSize(size);
            }}
            ActionsComponent={Mui.TablePaginationActions}
          />
        )}
      </Mui.Paper>
    </Mui.Box>
  );
}

Tanstack.propTypes = {
  columns: PropTypes.any,
  data: PropTypes.any,
  defaultPaginationOption: PropTypes.any,
  defaultSortOption: PropTypes.any,
  pageSizes: PropTypes.array,
  isPaginated: PropTypes.bool,
  isSortable: PropTypes.bool,
  columnsAlignment: PropTypes.object,
  customStyles: PropTypes.object,
  pagination: PropTypes.object,
  onPaginationChange: PropTypes.func,
};

export default Tanstack;
